/**
 * Pure helpers for the sales tracker.
 *
 * Kept out of the components so those files export only components and fast
 * refresh keeps working. Nothing here imports React and nothing here fetches.
 *
 * 🔴 **The tracker is DEALS now, not pursuits — owner's decision 2026-10-12.**
 * *SUPERSEDED: the priority vocabulary, the status drop-target rules, the
 * grouping parser and the optimistic-insert helper.* All of them served the
 * pursuit board and went with it; the deal stage moves through an ordinary
 * `PATCH /deals/{id}` and needs no client bookkeeping.
 */
import {
  CAPACITY_CATEGORIES,
  CAPACITY_STATE_BADGE,
  capacityState,
  categoryLine,
  type CapacityCategory,
} from '@/lib/project-capacity';
import type {
  SalesTrackerDealBoardParams,
  SalesTrackerDealLane,
  SalesTrackerDealLaneProject,
} from '@/lib/types';

/* ------------------------------------------------------------------ *
 * The band's capacity and target — owner 2026-10-27
 * ------------------------------------------------------------------ */

/** One category's figure in a band heading, with its state badge (or none). */
export interface LaneCategoryFigure {
  key: CapacityCategory;
  /** "Institutions 12 of 30 · target 20" — wording from `lib/project-capacity.ts`. */
  line: string;
  badge: { label: string; tone: 'success' | 'neutral' } | null;
}

/**
 * The band heading's per-category figures plus `Quoted`, or `null` when the API
 * predates the plan (`capacityPlan` or `counts.sold` undefined) — the caller then
 * falls back to the old `Booked N of C` heading built from {@link laneTargetLabel}.
 *
 * Sold = distinct companies with an accepted quote or a live invoice line on the
 * project; `quoted` = sent-quote companies not already sold (`null` when the
 * server omits it). Over capacity is a neutral badge, never a refusal.
 */
export function laneCapacityFigures(
  project: SalesTrackerDealLaneProject,
  counts: SalesTrackerDealLane['counts'],
): { categories: LaneCategoryFigure[]; quoted: number | null } | null {
  const plan = project.capacityPlan;
  const sold = counts.sold;
  if (plan === undefined || sold === undefined) return null;
  return {
    categories: CAPACITY_CATEGORIES.map(({ key }) => ({
      key,
      line: categoryLine(key, sold, plan),
      badge: CAPACITY_STATE_BADGE[capacityState(sold[key], plan[key])],
    })),
    quoted: counts.quoted ?? null,
  };
}

/* ------------------------------------------------------------------ *
 * The band's target — the FALLBACK for an API without `capacityPlan`
 * ------------------------------------------------------------------ */

/**
 * *SUPERSEDED as the main heading (2026-10-27) by {@link laneCapacityFigures};
 * kept for one release as the fallback when the server sends no plan.*
 *
 * A band's headline count against the project's capacity — `"30 of 27"`, or
 * `"7, no target"` where the project carries none.
 *
 * ⚠️ **The caller decides the word.** The band renders this under `Booked`; the
 * number handed in is `counts.accepted`.
 *
 * ⛔ **A `null` capacity MUST NOT print a denominator.** `null` means *no
 * target*, which is not a target of zero: `"7 / 0"` reads as sold out and
 * over-sold at once. It is also the MAJORITY of projects (measured 2026-09-11:
 * 78 of 97 carried none), so nothing may mark this branch as the unusual one —
 * no muted style, no badge, no "not set yet".
 *
 * `0` is a real measurement and prints as `0`.
 */
export function laneTargetLabel(count: number, capacity: number | null): string {
  if (capacity === null) return `${count}, no target`;
  return `${count} of ${capacity}`;
}

/**
 * Fallback only (see {@link laneTargetLabel}). Whether `counts.accepted` has
 * passed a REAL capacity.
 *
 * ⚠️ **Over capacity is NORMAL and this drives a neutral note, never an error
 * tone.** Nothing server-side refuses work for exceeding a project's capacity.
 * A `null` capacity is never over; a capacity of `0` genuinely is a target of
 * none, so a single accepted quote is over it.
 */
export function isOverCapacity(accepted: number, capacity: number | null): boolean {
  if (capacity === null) return false;
  return accepted > capacity;
}

/* ------------------------------------------------------------------ *
 * The project filter
 * ------------------------------------------------------------------ */

/**
 * The project filter, out of the URL.
 *
 * 🔴 **A BAD MEMBER IS RETURNED, NEVER DROPPED IN SILENCE.** `Number("1,2")` is
 * `NaN` and `Number("")` is `0`, so the natural one-liner turns a filter that
 * looks active in the control into one that is absent from the request. The
 * caller renders `invalid` and narrows on `ids` — refusing the whole filter
 * over one typo would take the board down, and forwarding the typo is a 422
 * naming `projectId` that does the same.
 *
 * Duplicates are collapsed and order is the URL's, which is the order the chips
 * read in.
 */
export function parseProjectIds(raw: string | null): { ids: number[]; invalid: string[] } {
  if (raw === null || raw.trim() === '') return { ids: [], invalid: [] };

  const ids: number[] = [];
  const invalid: string[] = [];
  const seen = new Set<number>();

  for (const part of raw.split(',')) {
    const token = part.trim();
    // An empty member — a trailing comma, or `1,,2`. Not a typo worth naming.
    if (token === '') continue;
    const parsed = Number(token);
    if (!Number.isSafeInteger(parsed) || parsed <= 0) {
      invalid.push(token);
      continue;
    }
    if (seen.has(parsed)) continue;
    seen.add(parsed);
    ids.push(parsed);
  }
  return { ids, invalid };
}

/**
 * The CSV to send, or `undefined` for "no filter".
 *
 * ⚠️ **`undefined`, never `''`.** An empty value on a `.strict()` query is a 422
 * rather than "every project".
 */
export function projectIdsParam(ids: readonly number[]): string | undefined {
  return ids.length === 0 ? undefined : ids.join(',');
}

/**
 * The single place the board's query is built, so the request and the address
 * bar cannot come to disagree about what a bare board means.
 *
 * ⛔ **No `groupBy`, ever.** The pursuit board's grouping switch is gone, and the
 * deal board's `.strict()` schema does not declare the key — sending it would be
 * a 422 that takes the whole board down.
 */
export function boardParamsOf(input: {
  cycleId: number;
  projectIds: readonly number[];
}): SalesTrackerDealBoardParams {
  const projectId = projectIdsParam(input.projectIds);
  return {
    cycleId: input.cycleId,
    ...(projectId === undefined ? {} : { projectId }),
  };
}
